import type { DemoDataSettings } from '../types/demoDataSettings';
import { DEFAULT_DEMO_DATA_SETTINGS } from '../types/demoDataSettings';

const STORAGE_KEY = 'demoDataSettings';
const STORAGE_VERSION = '1.0';

interface StoredDemoDataSettings {
  version: string;
  settings: DemoDataSettings;
  savedAt: string;
}

/**
 * Save demo data settings to localStorage
 */
export const saveDemoDataSettings = (settings: DemoDataSettings): void => {
  try {
    const storedSettings: StoredDemoDataSettings = {
      version: STORAGE_VERSION,
      settings,
      savedAt: new Date().toISOString(),
    };

    localStorage.setItem(STORAGE_KEY, JSON.stringify(storedSettings));
  } catch (error) {
    console.error('Failed to save demo data settings to localStorage:', error);
  }
};

/**
 * Load demo data settings from localStorage
 */
export const loadDemoDataSettings = (): DemoDataSettings | null => {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return null;
    }

    const parsed: StoredDemoDataSettings = JSON.parse(stored);

    // Check version compatibility
    if (parsed.version !== STORAGE_VERSION) {
      clearDemoDataSettings();
      return null;
    }

    // Merge with defaults to ensure all fields are present (for backward compatibility)
    const settings: DemoDataSettings = {
      ...DEFAULT_DEMO_DATA_SETTINGS,
      ...parsed.settings,
      // Ensure arrays and numbers are properly set
      clinicalSiteIds: parsed.settings.clinicalSiteIds || DEFAULT_DEMO_DATA_SETTINGS.clinicalSiteIds,
      numberOfRows: parsed.settings.numberOfRows || DEFAULT_DEMO_DATA_SETTINGS.numberOfRows,
      startSubjectNumber: parsed.settings.startSubjectNumber ?? DEFAULT_DEMO_DATA_SETTINGS.startSubjectNumber,
    };

    // Return the settings
    return settings;
  } catch (error) {
    console.error('Failed to load demo data settings from localStorage:', error);
    clearDemoDataSettings();
    return null;
  }
};

/**
 * Clear demo data settings from localStorage
 */
export const clearDemoDataSettings = (): void => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Failed to clear demo data settings from localStorage:', error);
  }
};

